import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, IconButton, Autocomplete, Grid, Divider, CircularProgress,  } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';

const NewInvoice = () => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([
    { product: null, description: '', quantity: 1, unit: 'متر', unit_price: 0 }
  ]);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await client.get('/users/customers/', { params: { page_size: 1000 } });
      setCustomers(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await client.get('/users/products/', { params: { page_size: 1000 } });
      setProducts(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fa-IR').format(num || 0);
  };

  const parseNumber = (str) => {
    if (!str) return 0;
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    const arabicDigits = '٠١٢٣٤٥٦٧٨٩';
    let result = String(str);
    for (let i = 0; i < 10; i++) {
      result = result.replace(new RegExp(persianDigits[i], 'g'), i);
      result = result.replace(new RegExp(arabicDigits[i], 'g'), i);
    }
    return parseFloat(result.replace(/,/g, '')) || 0;
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product: null, description: '', quantity: 1, unit: 'متر', unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.warning('حداقل یک آیتم باید وجود داشته باشد');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateItemTotal = (item) => {
    return (parseNumber(item.quantity) || 0) * (parseNumber(item.unit_price) || 0);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const calculateFinal = () => {
    return calculateTotal() - (parseNumber(discount) || 0);
  };

  const handleSubmit = async () => {
    // Validation
    const hasValidItem = items.some(item => item.description || item.product);
    if (!hasValidItem) {
      toast.error('حداقل یک آیتم با توضیحات یا کالا وارد کنید');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomer?.id || null,
        invoice_date: invoiceDate,
        status: 'Draft',
        discount: parseNumber(discount) || 0,
        notes: notes,
        items: items.map(item => ({
          product_id: item.product?.id || null,
          description: item.description || '',
          quantity: parseNumber(item.quantity) || 1,
          unit: item.unit || 'متر',
          unit_price: parseNumber(item.unit_price) || 0,
        })),
      };

      await client.post('/users/invoices/', payload);
      toast.success('فاکتور ذخیره شد');
      navigate('/shop/invoices');
    } catch (error) {
      toast.error('خطا در ذخیره فاکتور');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        فاکتور جدید
      </Typography>

      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => `${option.full_name} - ${option.mobile || ''}`}
                value={selectedCustomer}
                onChange={(e, value) => setSelectedCustomer(value)}
                renderInput={(params) => <CustomTextField {...params} label="مشتری (اختیاری)" />}
                noOptionsText="مشتری یافت نشد"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField                 type="date"
                label="تاریخ"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Items */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
        آیتم‌ها
      </Typography>

      {items.map((item, index) => (
        <Card key={index} sx={{ mb: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) => option.name}
                  value={item.product}
                  onChange={(e, value) => handleItemChange(index, 'product', value)}
                  renderInput={(params) => <CustomTextField {...params} label="کالا" size="small" />}
                  noOptionsText="کالا یافت نشد"
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField                   label="شرح"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <CustomTextField                   label="تعداد"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                  size="small"
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={4}>
                <CustomTextField                   label="واحد"
                  value={item.unit}
                  onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={4}>
                <CustomTextField                   label="قیمت واحد"
                  type="number"
                  value={item.unit_price}
                  onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2" color="text.secondary">
                  جمع: {formatNumber(calculateItemTotal(item))} ریال
                </Typography>
              </Grid>
              <Grid item xs={4} sx={{ textAlign: 'left' }}>
                <IconButton color="error" onClick={() => removeItem(index)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addItem}
        sx={{ mb: 2 }}
      >
        افزودن آیتم
      </Button>

      {/* Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CustomTextField                 label="تخفیف (ریال)"
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextField                 label="یادداشت"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>جمع کل:</Typography>
            <Typography>{formatNumber(calculateTotal())} ریال</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>تخفیف:</Typography>
            <Typography>{formatNumber(parseNumber(discount))} ریال</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight="bold">مبلغ نهایی:</Typography>
            <Typography fontWeight="bold" color="primary">
              {formatNumber(calculateFinal())} ریال
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Submit */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={handleSubmit}
        disabled={saving}
      >
        ذخیره فاکتور
      </Button>
    </Box>
  );
};

export default NewInvoice;
